import { useMemo } from 'react'

import { Role } from '../interfaces/rolesEnum'
import { useAppSelector } from './useAppSelector'

const global = {
  profile: true,
  dashboard: true,
  reports: true,
  users: true,
  stores: true,
  products: true,
  promotersCompany: true,
  logs: true,
  tradeMarketingDailyCheck: true,
  viewImages: true,
  editImages: true,
  upsertReport: true,
  editReportSellerData: true,
  upsertUser: true,
  removeRestoreUser: true,
  createStore: true,
  updateStore: true,
  updateStorePromoters: true,
  upsertProduct: true,
  upsertPromotersCompany: true,
  editStorePromotersCompany: true,
  upsertStoreGroup: true,
  seeAllPromoters: true,
  seeReportStoreSeller: true,
  canAddProductProblem: true,
  updateStoreSupervisor: true,
  updateStoreTradeMarketingCoordinator: true,
  isObserver: false,
  updatepromoterNotes: false,
  canExportReports: true,
  canGenerateTemporaryLink: true,
  canToggleTradeMarketingDailyCheck: false,
}

const permissions: Record<keyof typeof Role, typeof global> = {
  ROOT: {
    ...global,
    updatepromoterNotes: true,
    canToggleTradeMarketingDailyCheck: true,
  },
  ADMIN: { ...global },
  SUPERVISOR: {
    ...global,
    updateStoreSupervisor: false,
  },
  TRADE_MARKETING_COORDINATOR: {
    ...global,
    users: false,
    stores: false,
    products: false,
    promotersCompany: false,
    logs: false,
    removeRestoreUser: false,
    upsertUser: false,
    createStore: false,
    updateStore: false,
    updateStorePromoters: false,
    upsertProduct: false,
    upsertPromotersCompany: false,
    editStorePromotersCompany: false,
    upsertStoreGroup: false,
    seeAllPromoters: false,
    updateStoreTradeMarketingCoordinator: false,
    canToggleTradeMarketingDailyCheck: true,
    canGenerateTemporaryLink: false,
  },
  PROMOTER: {
    ...global,
    reports: false,
    users: false,
    stores: false,
    products: false,
    promotersCompany: false,
    logs: false,
    tradeMarketingDailyCheck: false,
    upsertReport: false,
    editReportSellerData: false,
    upsertUser: false,
    removeRestoreUser: false,
    createStore: false,
    updateStore: false,
    updateStorePromoters: false,
    upsertProduct: false,
    upsertPromotersCompany: false,
    editStorePromotersCompany: false,
    upsertStoreGroup: false,
    seeAllPromoters: false,
    seeReportStoreSeller: false,
    updateStoreSupervisor: false,
    updateStoreTradeMarketingCoordinator: false,
    updatepromoterNotes: true,
    canExportReports: false,
    canGenerateTemporaryLink: false,
  },
  PROMOTER_MANAGER: {
    ...global,
    reports: false,
    products: false,
    promotersCompany: false,
    tradeMarketingDailyCheck: false,
    logs: false,
    editImages: false,
    upsertReport: false,
    editReportSellerData: false,
    removeRestoreUser: false,
    createStore: false,
    updateStore: false,
    upsertProduct: false,
    upsertPromotersCompany: false,
    editStorePromotersCompany: false,
    upsertStoreGroup: false,
    canAddProductProblem: false,
    updateStoreSupervisor: false,
    updateStoreTradeMarketingCoordinator: false,
    updatepromoterNotes: true,
    seeReportStoreSeller: false,
    canExportReports: false,
    canGenerateTemporaryLink: false,
  },
  SELLER: {
    ...global,
    reports: false,
    users: false,
    stores: false,
    products: false,
    promotersCompany: false,
    tradeMarketingDailyCheck: false,
    logs: false,
    viewImages: false,
    editImages: false,
    upsertReport: false,
    upsertUser: false,
    removeRestoreUser: false,
    createStore: false,
    updateStore: false,
    updateStorePromoters: false,
    upsertPromotersCompany: false,
    editStorePromotersCompany: false,
    upsertProduct: false,
    upsertStoreGroup: false,
    seeAllPromoters: false,
    updateStoreSupervisor: false,
    updateStoreTradeMarketingCoordinator: false,
    canGenerateTemporaryLink: false,
  },
  OBSERVER: {
    ...global,
    seeAllPromoters: false,
    isObserver: true,
  },
}

export function usePermission() {
  const { profile } = useAppSelector(state => state.auth)
  const getPermissions = (role: Role, subrole?: Role) => {
    if (!subrole) return permissions[role]

    const rolePermissions = Object.entries(permissions[role]).map(([key, value]) => ({ [key]: value }))
    const subrolePermissions = Object.entries(permissions[subrole]).map(([key, value]) => ({ [key]: value }))

    const combinedPermissions: { [key: string]: boolean } = {}

    rolePermissions.forEach(permission => {
      Object.keys(permission).forEach(key => {
        combinedPermissions[key] = permission[key]
      })
    })

    subrolePermissions.forEach(permission => {
      Object.keys(permission).forEach(key => {
        if (combinedPermissions[key] === false && permission[key] === true) {
          combinedPermissions[key] = true
        }
      })
    })

    return combinedPermissions
  }

  return useMemo(() => getPermissions(profile?.role as Role, profile?.subrole as Role), [profile])
}
